"use client";

import { useMemo, useRef, useState } from "react";

import { Card } from "@/components/atoms/Card";
import { cn } from "@/lib/utils";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { buildFaq4ViewModel } from "./viewModel";

type Props = { data: { data?: Record<string, unknown> } & Record<string, unknown> };

const TRUNCATE_LENGTH = 200;

const VOID_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

const HEADING_TAGS = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);

/** Matches an HTML comment or a single start/end tag. */
const HTML_TOKEN = /<!--[\s\S]*?-->|<\/?[a-zA-Z][^>]*>/g;

/** Visible characters only — used both to decide on truncation and to measure it. */
function htmlToPlainText(html: string): string {
  return html.replace(/<!--[\s\S]*?-->/g, "").replace(/<[^>]*>/g, "");
}

/**
 * Trim rich-text HTML to ~maxLength visible characters, preserving tag nesting.
 *
 * Deliberately pure string work: this runs during SSR *and* during hydration, so
 * it must not touch `document`. The previous DOM-based version bailed out with
 * `if (typeof window === "undefined") return html`, so the server emitted the
 * full answer while the client computed a truncated one. React reports that as a
 * hydration mismatch on `dangerouslySetInnerHTML` and — because innerHTML
 * mismatches are "not patched up" — left the server's full text in the DOM, so
 * every collapsed card actually rendered expanded next to a "View more" button.
 * Tags are passed through verbatim (no attribute re-serialization) so authored
 * markup, entities included, survives byte-for-byte.
 */
function truncateHTML(html: string, maxLength: number): string {
  if (!html) return html;
  if (htmlToPlainText(html).length <= maxLength) return html;

  let out = "";
  let used = 0;
  let cursor = 0;
  let nothingEmittedYet = true;
  let done = false;
  const openTags: string[] = [];

  const appendText = (text: string) => {
    if (!text || done) return;
    const remaining = maxLength - used;
    if (text.length <= remaining) {
      out += text;
      used += text.length;
      nothingEmittedYet = false;
      return;
    }
    const cut = text.slice(0, remaining);
    const lastSpace = cut.lastIndexOf(" ");
    out += (lastSpace !== -1 ? cut.slice(0, lastSpace) : cut) + "...";
    used = maxLength;
    nothingEmittedYet = false;
    done = true;
  };

  HTML_TOKEN.lastIndex = 0;
  let token: RegExpExecArray | null;
  while (!done && (token = HTML_TOKEN.exec(html)) !== null) {
    appendText(html.slice(cursor, token.index));
    cursor = token.index + token[0].length;
    if (done) break;
    // Budget spent exactly on the preceding text: stop here rather than opening
    // another element we would only fill with the ellipsis. `done` also stops
    // the trailing appendText below from treating the leftover markup as text.
    if (used >= maxLength) {
      done = true;
      break;
    }

    const raw = token[0];
    if (raw.startsWith("<!--")) continue; // comments carry no visible text

    const isClosing = raw[1] === "/";
    const tagName = (raw.match(/^<\/?\s*([a-zA-Z][a-zA-Z0-9-]*)/) || [])[1]?.toLowerCase();
    if (!tagName) continue;

    if (isClosing) {
      // Close through to the matching ancestor; ignore strays with no opener.
      const depth = openTags.lastIndexOf(tagName);
      if (depth !== -1) {
        for (let i = openTags.length - 1; i >= depth; i -= 1) {
          out += `</${openTags[i]}>`;
        }
        openTags.length = depth;
      }
      continue;
    }

    // Drop a leading <br>, or one that only pads a heading (matches prior behaviour).
    if (tagName === "br") {
      const parentTag = openTags[openTags.length - 1];
      if (nothingEmittedYet || HEADING_TAGS.has(parentTag || "")) continue;
    }

    out += raw;
    nothingEmittedYet = false;
    if (!VOID_TAGS.has(tagName) && !raw.endsWith("/>")) openTags.push(tagName);
  }

  appendText(html.slice(cursor));

  for (let i = openTags.length - 1; i >= 0; i -= 1) {
    out += `</${openTags[i]}>`;
  }

  return out;
}

/**
 * `{...}` in the CMS heading marks a highlighted run — e.g. `Explore {More}`.
 * It reuses the "View more / View less" link color so the section keeps one
 * accent, and returns nodes (not HTML) since the heading is plain text.
 * An unmatched brace is left as-is rather than swallowing the rest of the line.
 */
function renderHighlightedTitle(title: string, color: string) {
  return title.split(/(\{[^{}]*\})/g).map((part, index) =>
    part.startsWith("{") && part.endsWith("}") ? (
      <span key={index} style={{ color }}>
        {part.slice(1, -1)}
      </span>
    ) : (
      part
    ),
  );
}

/**
 * FAQ variant 4 — "Explore more" style card list. Layout is fixed; all CMS
 * fields are normalized in `buildFaq4ViewModel`. Each card shows an optional
 * question title and a rich-text answer with a "View more / View less" toggle.
 */
export function Faq4Section({ data }: Props) {
  const ref = useRef<HTMLElement>(null);
  const vm = useMemo(() => buildFaq4ViewModel(data), [data]);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) FireWidgetViewedEvent(ref, "faqs_section");
  // }, []);

  // Same measurement `truncateHTML` uses, so the toggle appears exactly when
  // there is something hidden behind it.
  const shouldShowToggle = useMemo(
    () =>
      vm.cards.map(
        (card) => htmlToPlainText(card.answer).length > TRUNCATE_LENGTH,
      ),
    [vm.cards],
  );

  const toggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  if (vm.cards.length === 0) return null;

  const { section: backdrop } = vm;
  const hasBgImage = Boolean(backdrop.imageUrl);
  const hasTitle = Boolean(vm.header.title.trim());

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative w-full overflow-hidden",
      )}
      style={{ backgroundColor: backdrop.fallbackBg, fontFamily: "Poppins, sans-serif" }}
    >
      {hasBgImage ? (
        <div
          className="pointer-events-none absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${backdrop.imageUrl})` }}
          role={backdrop.imageAlt ? "img" : undefined}
          aria-label={backdrop.imageAlt || undefined}
          aria-hidden={backdrop.imageAlt ? undefined : true}
        />
      ) : null}

      <div
        className={cn(
          "relative z-[2] mx-auto flex w-full max-w-7xl flex-col",
          "px-5 py-8 sm:px-6 md:px-10 md:py-12 lg:px-8",
        )}
      >
        {hasTitle || vm.header.subtitle ? (
          <div className="mb-6 flex flex-col gap-2 md:mb-8">
            {hasTitle ? (
              <h2
                className="text-center text-xl font-semibold tracking-[-0.8px] sm:text-[32px] md:text-left"
                style={{ color: vm.header.titleColor }}
              >
                {renderHighlightedTitle(vm.header.title, vm.theme.viewMore)}
              </h2>
            ) : null}
            {vm.header.subtitle ? (
              <p
                className="text-center text-base font-normal sm:text-lg md:text-left"
                style={{ color: vm.header.subtitleColor }}
              >
                {vm.header.subtitle}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex w-full flex-col items-center justify-between gap-3 md:gap-4">
          {vm.cards.map((card, index) => {
            const isExpanded = expandedId === card.id;
            const isToggleable = shouldShowToggle[index];
            const displayContent = isExpanded ? card.answer : truncateHTML(card.answer, TRUNCATE_LENGTH);

            return (
              <Card
                key={card.id}
                className="relative w-full max-w-4xl rounded-md px-4 py-4 md:px-10 md:py-6"
                style={{ backgroundColor: card.background }}
              >
                {card.question ? (
                  <h3
                    className="mb-2 text-base font-semibold md:text-lg"
                    style={{ color: card.questionColor }}
                  >
                    {card.question}
                  </h3>
                ) : null}

                <div
                  className="text-sm font-normal leading-[160%] md:text-base"
                  style={{ color: card.answerColor }}
                >
                  <div
                    className={cn(
                      "h-auto overflow-hidden",
                      "[&_ol]:list-decimal [&_ol]:pl-5 [&_ul_li]:mt-2 [&_ul]:list-disc [&_ul]:pl-5",
                      "[&_a]:underline",
                    )}
                    dangerouslySetInnerHTML={{ __html: displayContent }}
                  />

                  {isToggleable ? (
                    <button
                      type="button"
                      onClick={() => toggleExpand(card.id)}
                      className="mt-1 inline-block font-semibold"
                      style={{ color: vm.theme.viewMore }}
                      aria-label={isExpanded ? "View less" : "View more"}
                    >
                      {isExpanded ? "View less" : "View more"}
                    </button>
                  ) : null}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
